import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Surface, Btn, Field, Input } from "../components/Primitives";
import PendingTypesCard from "../components/PendingTypesCard";
import {
  SuperuserFinanceCard, SuperuserPlanPricingCard,
  SuperuserDiscountCodesCard, SuperuserInviteClientCard,
} from "../components/SuperuserAdmin";
import { Buildings, ArrowLeft, Shield, Pause, Play, Clock, Sparkle, EnvelopeSimple, Airplane, Trash } from "@phosphor-icons/react";

const PLANS = ["free_beta", "legacy_beta", "starter", "standard", "pro", "enterprise"];
const STATUSES = ["active", "suspended", "trial", "cancelled"];
const CADENCES = ["monthly", "annual"];
const SUB_STATUSES = [
  "active", "trialing", "past_due", "unpaid",
  "canceled", "incomplete", "incomplete_expired",
];

export default function Superuser() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState(null);
  const [error, setError] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialResult, setTrialResult] = useState(null);
  // Phase 4G — EASA AD scraper
  const [easaBusy, setEasaBusy] = useState(false);
  const [easaResult, setEasaResult] = useState(null);
  const [easaStatus, setEasaStatus] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user?.platform_superuser) {
      navigate("/", { replace: true });
      return;
    }
    api.get("/superuser/tenants").then((r) => setTenants(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Load failed"));
    api.get("/superuser/ads/sync-easa/status")
      .then((r) => setEasaStatus(r.data))
      .catch(() => {});
  }, [user, loading, navigate]);

  const runTrialAlerts = async (dry) => {
    setTrialBusy(true); setTrialResult(null);
    try {
      const r = await api.post("/superuser/trial-alerts/run", { dry_run: dry });
      setTrialResult(r.data);
    } catch (e) {
      setTrialResult({ error: e.response?.data?.detail || "Run failed" });
    } finally {
      setTrialBusy(false);
    }
  };

  // Phase 4G — manually trigger the EASA AD scrape
  const runEasaSync = async (dry) => {
    setEasaBusy(true); setEasaResult(null);
    try {
      const r = await api.post("/superuser/ads/sync-easa", { dry_run: dry });
      setEasaResult(r.data);
      if (!dry) {
        const s = await api.get("/superuser/ads/sync-easa/status");
        setEasaStatus(s.data);
      }
    } catch (e) {
      setEasaResult({ error: e.response?.data?.detail || "Run failed" });
    } finally {
      setEasaBusy(false);
    }
  };

  if (loading || !tenants) return null;

  return (
    <div>
      <PageHeader
        overline="Platform"
        title="All tenants"
        subtitle="Every workspace on this installation. Suspend, resume, change plan, override billing, or trigger trial reminder emails."
      />
      <div className="p-8 space-y-6">
        {error && (
          <div className="p-3 border border-red-900/60 bg-red-950/20 text-xs text-red-300 font-mono">
            {error}
          </div>
        )}

        {/* Trial alerts runner */}
        <Surface className="p-6" data-testid="trial-alerts-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2 flex items-center gap-2">
                <EnvelopeSimple size={12} /> Trial expiry reminders
              </div>
              <div className="text-sm text-zinc-300">
                Daily cron sends T-7 / T-3 / T-1 / T-0 emails to every workspace on trial.
                Manual run available below.
              </div>
              <div className="text-[11px] text-zinc-500 mt-1.5 font-mono">
                Cron schedule: 07:00 UTC daily · dedupe key: (tenant, threshold, trial_ends_at)
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Btn variant="secondary" disabled={trialBusy} onClick={() => runTrialAlerts(true)}
                   data-testid="trial-alerts-dryrun-btn">
                Dry run
              </Btn>
              <Btn disabled={trialBusy} onClick={() => runTrialAlerts(false)}
                   data-testid="trial-alerts-run-btn">
                Run now
              </Btn>
            </div>
          </div>
          {trialResult && (
            <div className="mt-4 p-3 bg-zinc-950/60 border border-zinc-800 font-mono text-[11px] text-zinc-300"
                 data-testid="trial-alerts-result">
              {trialResult.error ? (
                <span className="text-red-300">{trialResult.error}</span>
              ) : (
                <span>
                  scanned: <span className="text-amber">{trialResult.tenants_scanned}</span> ·
                  sent: <span className="text-amber">{trialResult.alerts_sent}</span> ·
                  skipped (dedupe): <span className="text-zinc-400">{trialResult.skipped_already_sent}</span> ·
                  by threshold: <span className="text-zinc-400">{JSON.stringify(trialResult.by_threshold)}</span>
                  {trialResult.dry_run && <span className="ml-2 text-cyan-300">[dry-run]</span>}
                </span>
              )}
            </div>
          )}
        </Surface>

        {/* Phase 4G — EASA AD biweekly scraper */}
        <Surface className="p-6" data-testid="easa-sync-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2 flex items-center gap-2">
                <Airplane size={12} /> EASA Airworthiness Directives sync
              </div>
              <div className="text-sm text-zinc-300">
                Pulls the latest biweekly AD CSV from ad.easa.europa.eu,
                dedupes by reference, links each AD to matching aircraft types,
                and inserts into the global ADs library.
              </div>
              <div className="text-[11px] text-zinc-500 mt-1.5 font-mono">
                Cron schedule: 04:00 UTC daily · Last sync:{" "}
                <span className="text-amber" data-testid="easa-last-sync">
                  {easaStatus?.last_sync_at || "never"}
                </span>{" "}
                · Total scraped:{" "}
                <span className="text-amber" data-testid="easa-total-scraped">
                  {easaStatus?.total_scraped ?? "—"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Btn variant="secondary" disabled={easaBusy} onClick={() => runEasaSync(true)}
                   data-testid="easa-sync-dryrun-btn">
                Dry run
              </Btn>
              <Btn disabled={easaBusy} onClick={() => runEasaSync(false)}
                   data-testid="easa-sync-run-btn">
                Sync now
              </Btn>
            </div>
          </div>
          {easaResult && (
            <div className="mt-4 p-3 bg-zinc-950/60 border border-zinc-800 font-mono text-[11px] text-zinc-300"
                 data-testid="easa-sync-result">
              {easaResult.error ? (
                <span className="text-red-300">{easaResult.error}</span>
              ) : !easaResult.ok ? (
                <span className="text-red-300">Failed — {easaResult.reason}</span>
              ) : (
                <div className="space-y-2">
                  <div>
                    fetched: <span className="text-amber">{easaResult.fetched}</span> ·
                    created: <span className="text-emerald-300">+{easaResult.created}</span> ·
                    skipped (dedupe): <span className="text-zinc-400">{easaResult.skipped}</span> ·
                    linked-to-types: <span className="text-cyan-300">{easaResult.linked}</span>
                    {easaResult.dry_run && <span className="ml-2 text-cyan-300">[dry-run]</span>}
                  </div>
                  {easaResult.samples?.length > 0 && (
                    <div className="border-t border-zinc-800 pt-2">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">First {easaResult.samples.length} new ADs</div>
                      {easaResult.samples.map((s) => (
                        <div key={s.reference} className="flex items-baseline gap-2 py-0.5">
                          <span className="text-amber w-24 shrink-0">{s.reference}</span>
                          <span className="text-zinc-500 w-12 shrink-0">{s.authority}</span>
                          <span className="text-zinc-300 truncate flex-1">{s.title}</span>
                          {s.linked_aircraft_types?.length > 0 && (
                            <span className="text-cyan-300 text-[10px]">→ {s.linked_aircraft_types.join(", ")}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Surface>

        <PendingTypesCard />

        <SuperuserFinanceCard />
        <SuperuserPlanPricingCard />
        <SuperuserDiscountCodesCard />
        <SuperuserInviteClientCard />

        <Surface className="p-0 overflow-hidden" data-testid="tenants-table-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono border-b border-zinc-900">
                <th className="text-left p-4">Workspace</th>
                <th className="text-left p-4">Slug</th>
                <th className="text-left p-4">Plan</th>
                <th className="text-left p-4">Status</th>
                <th className="text-right p-4">Aircraft</th>
                <th className="text-right p-4">Users</th>
                <th></th>
              </tr>
            </thead>
            <tbody data-testid="tenants-table-body">
              {tenants.map((t) => (
                <tr key={t.id} className="border-b border-zinc-900/60 hover:bg-zinc-950/40">
                  <td className="p-4">
                    <div className="font-medium">{t.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{t.id}</div>
                  </td>
                  <td className="p-4 font-mono text-xs">/t/{t.slug}</td>
                  <td className="p-4 font-mono text-xs">
                    {t.plan}
                    {t.billing_cadence && (
                      <span className="text-zinc-500"> · {t.billing_cadence}</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span
                      className={
                        "inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider " +
                        (t.status === "active"
                          ? "bg-green-950/40 text-green-300 border border-green-900/60"
                          : t.status === "suspended"
                            ? "bg-red-950/40 text-red-300 border border-red-900/60"
                            : "bg-zinc-900 text-zinc-300 border border-zinc-800")
                      }
                    >
                      {t.status}
                    </span>
                    {t.subscription_status && (
                      <div className="text-[10px] font-mono text-zinc-500 mt-1">{t.subscription_status}</div>
                    )}
                  </td>
                  <td className="p-4 text-right font-mono text-xs">{t._aircraft_count}/{t.aircraft_cap ?? "∞"}</td>
                  <td className="p-4 text-right font-mono text-xs">{t._active_users}/{t._user_count}</td>
                  <td className="p-4 text-right">
                    <div className="flex gap-2 justify-end items-center">
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          const disabled = t.subscription_status === "canceled_pending";
                          const url = disabled ? "enable" : "disable";
                          if (!confirm(`${disabled ? "Re-enable" : "Disable"} ${t.name}?`)) return;
                          await api.post(`/superuser/tenants/${t.id}/${url}`);
                          const r = await api.get("/superuser/tenants");
                          setTenants(r.data);
                        }}
                        className={`text-[10px] uppercase tracking-wider font-mono px-2 py-1 border ${t.subscription_status === "canceled_pending" ? "text-green-300 border-green-900/60 hover:bg-green-950/40" : "text-amber border-amber/40 hover:bg-amber/10"}`}
                        data-testid={`tenant-toggle-${t.id}`}
                      >
                        {t.subscription_status === "canceled_pending" ? "Enable" : "Disable"}
                      </button>
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          const name = prompt(`Type the workspace name "${t.name}" to PERMANENTLY DELETE this tenant + all its data. This cannot be undone.`);
                          if (name !== t.name) { if (name !== null) alert("Name did not match — deletion cancelled."); return; }
                          await api.delete(`/superuser/tenants/${t.id}`, { headers: { "X-Confirm-Tenant-Name": t.name } });
                          const r = await api.get("/superuser/tenants");
                          setTenants(r.data);
                        }}
                        className="text-zinc-500 hover:text-red-400 p-1"
                        title="Delete tenant + all data"
                        data-testid={`tenant-delete-${t.id}`}
                      >
                        <Trash size={14} />
                      </button>
                      <Link to={`/superuser/tenants/${t.id}`}
                            className="text-xs text-amber hover:underline"
                            data-testid={`open-tenant-${t.id}`}>Open →</Link>
                    </div>
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-zinc-500 text-sm">No tenants yet.</td></tr>
              )}
            </tbody>
          </table>
        </Surface>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tenant detail
// ─────────────────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}
function toDateInput(iso) {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
}

export function SuperuserTenantDetail() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [trialDateDraft, setTrialDateDraft] = useState("");
  const [discountDraft, setDiscountDraft] = useState("");
  const [priceDraft, setPriceDraft] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user?.platform_superuser) {
      navigate("/", { replace: true });
      return;
    }
    api.get(`/superuser/tenants/${tid}`).then((r) => {
      setT(r.data);
      setTrialDateDraft(toDateInput(r.data.trial_ends_at));
      setDiscountDraft(r.data.discount_pct != null ? String(r.data.discount_pct) : "");
      setPriceDraft(r.data.custom_price_eur_override != null ? String(r.data.custom_price_eur_override) : "");
    }).catch((e) => setErr(e.response?.data?.detail || "Load failed"));
  }, [tid, user, authLoading, navigate]);

  const patch = async (update) => {
    setBusy(true); setErr(null);
    try {
      const r = await api.patch(`/superuser/tenants/${tid}`, update);
      setT({ ...t, ...r.data });
    } catch (e) {
      setErr(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const extendTrial = async (days) => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/superuser/tenants/${tid}/extend-trial`, { days });
      setT({ ...t, ...r.data });
      setTrialDateDraft(toDateInput(r.data.trial_ends_at));
    } catch (e) {
      setErr(e.response?.data?.detail || "Extend failed");
    } finally {
      setBusy(false);
    }
  };

  const grantPro = async (cadence) => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/superuser/tenants/${tid}/grant-pro`, { cadence });
      setT({ ...t, ...r.data });
    } catch (e) {
      setErr(e.response?.data?.detail || "Grant failed");
    } finally {
      setBusy(false);
    }
  };

  const flags = t?.feature_flags || {};

  if (!t) return null;

  return (
    <div>
      <div className="px-8 pt-8">
        <Link to="/superuser" className="text-xs text-zinc-400 hover:text-amber inline-flex items-center gap-1.5"
              data-testid="back-to-tenants">
          <ArrowLeft size={14} /> Back to all tenants
        </Link>
      </div>
      <PageHeader
        overline="Tenant"
        title={t.name}
        subtitle={<>ID <span className="font-mono text-xs">{t.id}</span> · Slug <span className="font-mono text-xs">/t/{t.slug}</span></>}
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-1 md:grid-cols-2 gap-6">
        {/* Card 1 — workspace status & plan */}
        <Surface className="p-6 space-y-4" data-testid="card-status-plan">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Status & plan</div>
          <Field label="Workspace status">
            <select value={t.status} onChange={(e) => patch({ status: e.target.value })}
                    disabled={busy}
                    className="w-full h-10 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm font-mono"
                    data-testid="tenant-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Plan">
            <select value={t.plan} onChange={(e) => patch({ plan: e.target.value })}
                    disabled={busy}
                    className="w-full h-10 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm font-mono"
                    data-testid="tenant-plan-select">
              {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Aircraft cap (0 = unlimited)">
            <Input
              type="number" min="0" value={t.aircraft_cap ?? 0}
              onChange={(e) => patch({ aircraft_cap: parseInt(e.target.value, 10) || 0 })}
              disabled={busy}
              data-testid="tenant-cap-input"
            />
          </Field>
          <div className="flex gap-2 pt-2">
            {t.status !== "suspended" ? (
              <Btn variant="danger" onClick={() => patch({ status: "suspended" })}
                   disabled={busy} data-testid="suspend-tenant-btn">
                <Pause size={14} className="inline mr-1" /> Suspend workspace
              </Btn>
            ) : (
              <Btn onClick={() => patch({ status: "active" })}
                   disabled={busy} data-testid="resume-tenant-btn">
                <Play size={14} className="inline mr-1" /> Resume workspace
              </Btn>
            )}
          </div>
        </Surface>

        {/* Card 2 — Phase 4E billing overrides */}
        <Surface className="p-6 space-y-4" data-testid="card-billing-overrides">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono flex items-center gap-2">
            <Clock size={12} /> Billing overrides
          </div>
          <Field label="Billing cadence">
            <select value={t.billing_cadence || ""} onChange={(e) => patch({ billing_cadence: e.target.value || null })}
                    disabled={busy}
                    className="w-full h-10 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm font-mono"
                    data-testid="tenant-cadence-select">
              <option value="">— none —</option>
              {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Subscription status">
            <select value={t.subscription_status || ""}
                    onChange={(e) => patch({ subscription_status: e.target.value || null })}
                    disabled={busy}
                    className="w-full h-10 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm font-mono"
                    data-testid="tenant-sub-status-select">
              <option value="">— none —</option>
              {SUB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label={`Trial ends at — currently ${fmtDate(t.trial_ends_at)}`}>
            <div className="flex gap-2">
              <Input
                type="date" value={trialDateDraft}
                onChange={(e) => setTrialDateDraft(e.target.value)}
                disabled={busy}
                className="flex-1"
                data-testid="tenant-trial-date-input"
              />
              <Btn variant="secondary" disabled={busy || !trialDateDraft}
                   onClick={() => patch({ trial_ends_at: trialDateDraft })}
                   data-testid="tenant-trial-date-save-btn">Set</Btn>
            </div>
          </Field>
          <div className="flex flex-wrap gap-2 pt-1">
            <Btn variant="secondary" disabled={busy} onClick={() => extendTrial(7)}
                 data-testid="tenant-trial-extend-7-btn">+7 days</Btn>
            <Btn variant="secondary" disabled={busy} onClick={() => extendTrial(30)}
                 data-testid="tenant-trial-extend-30-btn">+30 days</Btn>
            <Btn variant="secondary" disabled={busy} onClick={() => extendTrial(90)}
                 data-testid="tenant-trial-extend-90-btn">+90 days</Btn>
          </div>
          <div className="pt-3 mt-3 border-t border-zinc-800 space-y-3">
            <Field label="Discount % (informational)">
              <div className="flex gap-2">
                <Input
                  type="number" min="0" max="100" step="1" value={discountDraft}
                  onChange={(e) => setDiscountDraft(e.target.value)}
                  disabled={busy}
                  className="flex-1"
                  data-testid="tenant-discount-input"
                />
                <Btn variant="secondary" disabled={busy}
                     onClick={() => patch({ discount_pct: discountDraft === "" ? null : parseFloat(discountDraft) })}
                     data-testid="tenant-discount-save-btn">Save</Btn>
              </div>
            </Field>
            <Field label="Custom price € / aircraft / period (override)">
              <div className="flex gap-2">
                <Input
                  type="number" min="0" step="0.01" value={priceDraft}
                  onChange={(e) => setPriceDraft(e.target.value)}
                  disabled={busy}
                  className="flex-1"
                  data-testid="tenant-custom-price-input"
                />
                <Btn variant="secondary" disabled={busy}
                     onClick={() => patch({ custom_price_eur_override: priceDraft === "" ? null : parseFloat(priceDraft) })}
                     data-testid="tenant-custom-price-save-btn">Save</Btn>
              </div>
            </Field>
          </div>
          <div className="pt-3 mt-3 border-t border-zinc-800 flex flex-wrap gap-2">
            <Btn disabled={busy} onClick={() => grantPro("monthly")}
                 data-testid="tenant-grant-pro-monthly-btn">
              <Sparkle size={14} className="inline mr-1" /> Grant Pro · monthly
            </Btn>
            <Btn disabled={busy} onClick={() => grantPro("annual")}
                 data-testid="tenant-grant-pro-annual-btn">
              <Sparkle size={14} className="inline mr-1" /> Grant Pro · annual
            </Btn>
          </div>
        </Surface>

        {/* Card 3 — feature flags */}
        <Surface className="p-6 space-y-2" data-testid="card-feature-flags">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono flex items-center gap-2">
            <Shield size={12} /> Feature flags
          </div>
          {["cost_sharing", "ad_compliance", "ai_doc_ingestion", "sms_alerts"].map((k) => (
            <label key={k} className="flex items-center justify-between text-sm hover:bg-zinc-950/60 -mx-3 px-3 py-2">
              <div>
                <div className="font-mono">{k}</div>
                <div className="text-[10px] text-zinc-500">{describeFlag(k)}</div>
              </div>
              <input
                type="checkbox"
                checked={!!flags[k]}
                onChange={(e) => patch({ feature_flags: { [k]: e.target.checked } })}
                disabled={busy}
                className="accent-amber w-4 h-4"
                data-testid={`flag-${k}`}
              />
            </label>
          ))}
        </Surface>

        {/* Card 4 — quick stats */}
        <Surface className="p-6" data-testid="card-stats">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-3 flex items-center gap-2">
            <Buildings size={12} /> Quick stats
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <Stat label="Aircraft" value={t._aircraft_count} />
            <Stat label="Active users" value={t._active_users} />
            <Stat label="Total users" value={t._user_count} />
          </div>
        </Surface>

        {t._recent_audit && t._recent_audit.length > 0 && (
          <Surface className="p-6 lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-3">
              Recent audit
            </div>
            <div className="space-y-2 max-h-72 overflow-auto">
              {t._recent_audit.map((row, i) => (
                <div key={i} className="text-xs font-mono text-zinc-400 flex items-center gap-3 py-1 border-b border-zinc-900/60">
                  <span className="text-zinc-600">{row.timestamp?.slice(0, 19)}</span>
                  <span className="text-zinc-300">{row.action}</span>
                  <span className="text-zinc-500">{row.target}</span>
                </div>
              ))}
            </div>
          </Surface>
        )}

        {err && (
          <div className="lg:col-span-2 p-3 border border-red-900/60 bg-red-950/20 text-xs text-red-300 font-mono"
               data-testid="superuser-error">
            {err}
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="border border-zinc-900 py-4">
    <div className="text-3xl font-semibold tabular-nums">{value ?? "—"}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mt-1">{label}</div>
  </div>
);

function describeFlag(k) {
  return {
    cost_sharing: "Original cost-sharing add-on (legacy module)",
    ad_compliance: "Airworthiness Directive ingestion + tracking",
    ai_doc_ingestion: "Manual ingestion + RAG assistant",
    sms_alerts: "SMS alert delivery (Twilio)",
  }[k] || "";
}
